/**
 * useAdminPermissions — derives admin-portal permission checks from
 * the current user's platform role, with a dev-mode permissive
 * fallback.
 *
 * The admin surfaces gate on permission, not role. This hook is the
 * single source of truth for those checks across Companies / Staff /
 * Imports / Audit. Pure derivation via `PERMISSIONS_BY_ROLE` —
 * deterministic, easy to test.
 *
 * Three exports:
 *   - `useAdminPermissions()` — returns `{ can, canAny, canAll }`.
 *   - `useCanAdmin(slug)` — convenience wrapper for the common
 *     single-slug check.
 *   - `setAdminPermissionsDevModeOverrideForTests(boolean | null)` —
 *     test-only escape hatch that lets specs exercise both the
 *     production-false branch and the dev-permissive branch without
 *     touching `import.meta.env`. Passing `null` restores live
 *     env-driven behavior. Production code MUST NOT call this — the
 *     name is intentionally long + ugly.
 *
 * Permissive dev fallback: when `me.platform` is null (a genuinely
 * non-platform user) AND the build is dev, the hook logs a one-shot
 * console warning and returns `true` for every `can()` call. That's
 * fine for development but NOT for production — in production the
 * same null-platform state returns `false` for every check.
 */

import * as React from 'react';
import {
  type AdminPermissionSlug,
  rolePermits,
} from './admin-permissions.js';
import { useMe } from './use-me.js';

export interface AdminPermissionChecks {
  readonly can: (permission: AdminPermissionSlug) => boolean;
  readonly canAny: (
    permissions: ReadonlyArray<AdminPermissionSlug>,
  ) => boolean;
  readonly canAll: (
    permissions: ReadonlyArray<AdminPermissionSlug>,
  ) => boolean;
}

// ── Dev-mode resolution ─────────────────────────────────────────────
//
// `import.meta.env.DEV` is the Vite-standard dev-mode signal. Tests
// can override via `setAdminPermissionsDevModeOverrideForTests(...)`
// to exercise both branches without hot-swapping the env.

let devModeOverride: boolean | null = null;

function isDevelopmentMode(): boolean {
  if (devModeOverride !== null) return devModeOverride;
  // `import.meta.env` is provided by Vite at build time. Defensive
  // access so the hook doesn't crash if loaded in an unusual runtime
  // (SSR shim, jest-like harness, etc.).
  try {
    return (
      (import.meta as unknown as { env?: { DEV?: unknown } }).env?.DEV === true
    );
  } catch {
    return false;
  }
}

/**
 * Test-only escape hatch. Pass `true` / `false` to force a dev-mode
 * decision, or `null` to restore live env-driven behavior. Production
 * code must not call this.
 */
export function setAdminPermissionsDevModeOverrideForTests(
  value: boolean | null,
): void {
  devModeOverride = value;
}

// ── One-shot warning state ──────────────────────────────────────────
//
// The permissive fallback warning fires once per session to avoid
// console spam. Reset via the test override hook.

let devFallbackWarningEmitted = false;

function emitDevFallbackWarningOnce(): void {
  if (devFallbackWarningEmitted) return;
  devFallbackWarningEmitted = true;
  if (typeof console !== 'undefined' && typeof console.warn === 'function') {
    console.warn(
      '[useAdminPermissions] No platform role on the session; dev mode is ' +
        'returning true for all admin permission checks. Production behavior ' +
        'is the inverse — every check returns false for non-platform users.',
    );
  }
}

/**
 * Reset the one-shot warning latch. Tests call this between cases so
 * the warning fires per-test. Not exported to production code.
 */
export function resetAdminPermissionsWarningForTests(): void {
  devFallbackWarningEmitted = false;
}

// ── Hook ────────────────────────────────────────────────────────────

export function useAdminPermissions(): AdminPermissionChecks {
  const me = useMe();
  const role = me?.platform?.role;

  return React.useMemo<AdminPermissionChecks>(() => {
    const can = (permission: AdminPermissionSlug): boolean => {
      if (role !== undefined) return rolePermits(role, permission);
      if (isDevelopmentMode()) {
        emitDevFallbackWarningOnce();
        return true;
      }
      return false;
    };
    return {
      can,
      canAny: (permissions) => permissions.some(can),
      canAll: (permissions) =>
        permissions.length > 0 && permissions.every(can),
    };
  }, [role]);
}

/**
 * Convenience hook for the common single-slug check. Equivalent to
 * `useAdminPermissions().can(slug)` but a touch easier to read at
 * the call site:
 *
 *   const canInviteStaff = useCanAdmin('staff:invite');
 */
export function useCanAdmin(permission: AdminPermissionSlug): boolean {
  return useAdminPermissions().can(permission);
}
